export type { MembershipEnvironment, MembershipCoupon, MembershipSummary } from './membership.js';
export type { MallPricing, MallFixedProductSlot, MallFixedProducts, MerchantMallPurchaseSummary, MerchantMallOrderRecord, MerchantMallOrderPage, AdminMerchantMallSummary, AdminMerchantMallDetail, MallProduct, MallPage, MallProducts, MallProductQuery, MallQuote, MallOrder, MallVoucher, MallSettings, MallFinancialLog, MallSummary, MallCatalogImportResult, MallCheckout, MallH5Availability } from './mall.js';
export type { MallVipUser, MallVipGrant, MallVipMerchant, MallVipPage } from './mall-vip.js';
export { mallCatalogCategories, mallCatalogWears, hasExcludedMallCatalogName, isOrdinaryMallCatalogItem, isMallCatalogItemEligible } from './mall.js';
export { canValueInventory, withoutUnavailableInventoryPrice, inventoryValueCny, compareInventoryPrice } from './inventory-valuation';
export { dotaQuality, dotaQualityDefinitions, dotaVariantNames } from './dota-quality';
export type { DotaMarketCatalogItem, DotaCatalogDetail } from './dota-quality';
export { IOS_COMPANION_TOKEN_PREFIX, isCompanionTransactionRequest } from './companion-policy';

export const gameConfig = {
  cs2: { appId: 730, contextId: '2', label: 'CS2', accent: '#d6ff3f' },
  dota2: { appId: 570, contextId: '2', label: 'Dota 2', accent: '#be5342' },
  tf2: { appId: 440, contextId: '2', label: 'TF2', accent: '#ff8a4c' },
} as const;

export type NotificationCategory = 'trade' | 'system' | 'activity';

export type UserNotification = {
  id: string;
  category: NotificationCategory;
  title: string;
  body: string;
  actionLabel?: string;
  actionHref?: string;
  createdAt: string;
  readAt?: string;
};

export type NotificationInbox = {
  items: UserNotification[];
  unreadCount: number;
};

export type GameId = keyof typeof gameConfig;
export type MarketCategoryKey = { game: GameId; marketHashName: string };
export type MarketCategorySummary = MarketCategoryKey & { count: number; lowestPriceFen?: number };
export type ItemRarity = 'consumer' | 'industrial' | 'milSpec' | 'restricted' | 'classified' | 'covert';
export type SteamInspectionStatus = 'exact' | 'not_applicable' | 'unavailable';
export type SteamInspectionSource = 'steam_asset_properties' | 'masked_link' | 'inspection_api' | 'steam_description';
export type DotaMetadata = { hero: string; rarity: string; quality: string; slot: string; type: string };
export type ReferencePrice = { amount: number; currency: "USD" | "CNY"; source: string; updatedAt: string; url: string; stale?: boolean; conversion?: { originalAmount: number; originalCurrency: "USD"; rate: number; source: string; updatedAt: string } };
export type InventoryItem = {
  dotaMetadata?: DotaMetadata;
  referencePrice?: ReferencePrice;
  inventoryVerified?: boolean;
  id: string;
  assetId: string;
  game: GameId;
  marketHashName?: string;
  name: string;
  subtitle: string;
  category?: string;
  wear?: string;
  /** Original quality/type text from the catalog or Steam, not a placeholder rarity. */
  quality?: string;
  rarity: ItemRarity;
  tradable: boolean;
  marketable?: boolean;
  priceCny: number;
  color: string;
  imageUrl?: string;
  listings?: number;
  trend24h?: number;
  floatValue?: number;
  paintSeed?: number;
  inspectLink?: string;
  inspectionStatus?: SteamInspectionStatus;
  inspectionSource?: SteamInspectionSource;
  inspectionError?: string;
  tradableAfter?: string;
  tradeRestrictionReason?: string;
  decorations?: Array<{
    type: 'sticker' | 'patch' | 'keychain';
    name: string;
    imageUrl?: string;
    slot?: number;
    steamId?: number;
    wear?: number;
  }>;
};
export type Cs2CatalogCategory =
  | '步枪'
  | '狙击步枪'
  | '冲锋枪'
  | '霰弹枪'
  | '机枪'
  | '手枪'
  | '刀具'
  | '手套'
  | '印花与布章'
  | '涂鸦'
  | '探员'
  | '武器箱'
  | '钥匙'
  | '挂件'
  | '音乐盒'
  | '收藏品'
  | '其他';
export type Cs2CatalogItem = {
  id: string;
  marketHashName: string;
  displayName: string;
  category: Cs2CatalogCategory;
  wear: string;
  rarity: string;
  imageUrl: string;
  priceCny?: number;
  listings?: number;
  pricePlatform?: string;
  priceUpdatedAt?: number;
};
export type Cs2CatalogQuery = {
  page: number;
  pageSize: number;
  query?: string;
  category?: Cs2CatalogItem['category'];
  wear?: string;
  rarity?: string;
  minPriceCny?: number;
  maxPriceCny?: number;
  sort?: 'catalog' | 'price_asc' | 'price_desc' | 'listings_desc';
};
export type Cs2CatalogPage = {
  source: 'steamdt' | 'unconfigured' | 'stale';
  syncedAt: string;
  items: Cs2CatalogItem[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  diagnostic?: string;
  mallSync?: {ordinaryTotal:number;pricedTotal:number;eligibleTotal:number;pendingTotal:number;enabled:boolean};
};
export type Cs2PlatformOffer = {
  wear: string;
  marketHashName: string;
  platform: string;
  priceCny: number;
  listings: number;
  biddingPriceCny?: number;
  bids?: number;
  updatedAt: number;
};
export type Cs2CatalogDetail = {
  source: 'steamdt' | 'unconfigured' | 'stale';
  syncedAt: string;
  item: Cs2CatalogItem;
  wearOptions: Array<{ id: string; wear: string; marketHashName: string; lowestPriceCny?: number; listings: number }>;
  offers: Cs2PlatformOffer[];
  diagnostic?: string;
};
export type ImportedSteamInventoryItem = {
  dotaMetadata?: DotaMetadata;
  referencePrice?: ReferencePrice;
  /** False for historical rows absent from the latest authoritative response. */
  inventoryVerified?: boolean;
  game: GameId;
  assetId: string;
  classId: string;
  instanceId: string;
  marketHashName: string;
  displayName: string;
  type: string;
  iconUrl: string;
  tradable: boolean;
  marketable: boolean;
  amount: number;
  priceCny?: number;
  pricePlatform?: string;
  inspectLink?: string;
  floatValue?: number;
  paintSeed?: number;
  inspectionStatus?: SteamInspectionStatus;
  inspectionSource?: SteamInspectionSource;
  inspectionError?: string;
  decorations?: InventoryItem['decorations'];
  tradableAfter?: string;
  tradeRestrictionReason?: string;
};
/** @deprecated Use ImportedSteamInventoryItem. Kept for older web clients during rollout. */
export type ImportedCs2InventoryItem = ImportedSteamInventoryItem;
export type SteamInventoryInspectionSummary = {
  applicable: number;
  exact: number;
  unavailable: number;
  notApplicable: number;
};
export type MarketActivity = {
  marketHashName: string;
  recent: Array<{ priceFen: number; completedAt: string }>;
  daily: Array<{ date: string; count: number; averageFen: number; lowFen: number; highFen: number }>;
};

export type MarketBookFilters = {game?:GameId|undefined;minFloat?:number|undefined;maxFloat?:number|undefined;paintSeed?:number|undefined;sticker?:string|undefined};
export type MarketBookSort = 'price_asc' | 'price_desc' | 'wear_asc' | 'wear_desc';
export type MarketBookPage = { items: MarketplaceListing[]; total: number; lowest?: MarketplaceListing; nextOffset?: number };

export type SteamInventoryImportResult = {
  snapshotComplete?: boolean;
  binding: SteamBindingSummary;
  items: ImportedSteamInventoryItem[];
  priceSource: 'steamdt' | 'unconfigured';
  inspection: SteamInventoryInspectionSummary;
};
export type SteamBrowserInventoryProperty = {
  assetId: string;
  contextId: '2' | '16';
  inspectPayload?: string | undefined;
  classId?: string | undefined;
  instanceId?: string | undefined;
  marketHashName?: string | undefined;
  displayName?: string | undefined;
  type?: string | undefined;
  iconUrl?: string | undefined;
  amount?: number | undefined;
  tradableAfter?: string | undefined;
};
export type SteamBrowserInventoryPropertySyncInput = {
  steamId: string;
  items: SteamBrowserInventoryProperty[];
};
export type SteamBindingSummary = {
  userId: string;
  steamId: string;
  status: 'bound' | 'inventory_private' | 'import_failed';
  itemCount: number;
  boundAt: string;
  lastImportedAt?: string;
};
export type SteamApiKeySummary = {
  status: 'unbound' | 'valid';
  keyHint?: string;
  lastValidatedAt?: string;
  updatedAt?: string;
};
export type TradeOfferStatus = 'draft' | 'pending_confirmation' | 'sent' | 'accepted' | 'completed' | 'cancelled' | 'failed' | 'expired';
export type TradeOffer = {
  id: string;
  game: GameId;
  status: TradeOfferStatus;
  itemIds: string[];
  itemCount: number;
  totalCny: number;
  createdAt: string;
  steamOfferId?: string;
};
export type CreateTradeOfferInput = { game: GameId; itemIds: string[]; idempotencyKey: string };
export type ApiEnvelope<T> = { data: T; requestId: string };

export type ClientPlatform = 'ios' | 'android';
export type SteamSessionStatus = 'unbound' | 'healthy' | 'refresh_required' | 'expired' | 'refreshing';
export type SteamSessionSummary = {
  id: string;
  steamId: string;
  platform: ClientPlatform;
  status: SteamSessionStatus;
  cookieVersion: number;
  schemaVersion: 2;
  lastValidatedAt: string;
  expiresAt: string;
  updatedAt: string;
};
export type SteamCookieSyncInput = {
  steamId: string;
  platform: ClientPlatform;
  encryptedEnvelope: string;
  cookieNames: string[];
  expiresAt: string;
  schemaVersion: 2;
};
export type SteamSessionRefreshInput = {
  steamId: string;
  reason: 'manual' | 'expired' | 'app_update' | 'server_rejected';
  clientVersion: string;
};
export type SteamSessionReceipt = {
  schemaVersion: 2;
  sessionId: string;
  steamId: string;
  status: SteamSessionStatus;
  lastValidatedAt: string;
  expiresAt: string;
};

export type WalletAccountType = 'purchase' | 'buy_order';
export type WalletAccountPolicy = {
  accountType: WalletAccountType;
  rechargeChannelGroup: 'purchase' | 'buy_order';
  withdrawalFeeMode: 'configured' | 'original_refund_only';
};
export type WalletBalance = {
  accountId: string;
  accountType: WalletAccountType;
  availableFen: number;
  frozenFen: number;
  version: number;
};
export type WalletOperation = 'recharge' | 'payment' | 'hold' | 'protected_credit' | 'capture' | 'release' | 'refund' | 'original_refund' | 'withdrawal' | 'adjustment';
export type WalletLedgerEntry = {
  id: string;
  accountType: WalletAccountType;
  operation: WalletOperation;
  availableDeltaFen: number;
  frozenDeltaFen: number;
  availableAfterFen: number;
  frozenAfterFen: number;
  businessId: string;
  createdAt: string;
};
export type WalletStatement = {
  balance: WalletBalance;
  entries: WalletLedgerEntry[];
};
export type WalletCommand = {
  userId: string;
  accountType: WalletAccountType;
  operation: WalletOperation;
  amountFen: number;
  businessId: string;
  idempotencyKey: string;
};

export type { ShouqianbaPaymentDetails } from './payment-details.js';
import type { ShouqianbaPaymentDetails } from './payment-details.js';
export type PaymentChannel = 'alipay_page' | 'huifu_alipay' | 'shouqianba';
export type PaymentOrderStatus = 'pending' | 'paid' | 'closed' | 'refunded';
export type PaymentOrder = {
  orderNo: string;
  userId: string;
  accountType: WalletAccountType;
  channel: PaymentChannel;
  amountFen: number;
  status: PaymentOrderStatus;
  subject: string;
  alipayTradeNo?: string;
  providerTradeNo?: string;
  paymentDetails?: ShouqianbaPaymentDetails;
  createdAt: string;
  expiresAt: string;
  paidAt?: string;
  refundedAmountFen?: number;
};
export type PaymentCheckout = { order: PaymentOrder; redirectUrl: string };
export type AppPaymentReturnScheme = 'redd' | 'redd-test';
export type AppPaymentAvailability = { available: boolean; provider: 'huifu'; method: 'alipay_cashier' };

export type OpenApiMerchantStatus = 'active' | 'disabled';
export type OpenApiMerchantMode = 'test' | 'production';
export type OpenApiMerchant = {
  id: string;
  appId: string;
  name: string;
  userId: string;
  accountType: WalletAccountType;
  mode: OpenApiMerchantMode;
  status: OpenApiMerchantStatus;
  singleLimitFen: number;
  dailyLimitFen: number;
  withdrawalFeeBps: number;
  ipAllowlist: string[];
  webhookUrl?: string;
  activeKeyId?: string;
  keyCreatedAt?: string;
  keyLastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
};
export type OpenApiWithdrawalOrder = {
  partnerOrderNo: string;
  withdrawalNo: string;
  status: FinancialOrderStatus;
  accountType: WalletAccountType;
  amountFen: number;
  feeFen: number;
  payoutFen: number;
  payeeIdentityMasked: string;
  payeeNameMasked: string;
  providerStatus?: string;
  failureCode?: string;
  failureMessage?: string;
  createdAt: string;
  completedAt?: string;
};
export type OpenApiIssuedCredentials = {
  appId: string;
  keyId: string;
  apiSecret: string;
  webhookSecret: string;
};
export type OpenApiMerchantProvisioning = {
  merchant: OpenApiMerchant;
  credentials: OpenApiIssuedCredentials;
};
export type OpenApiRechargeOrder = {
  provider?: PaymentChannel;
  providerTradeNo?: string;
  paymentDetails?: ShouqianbaPaymentDetails;
  partnerOrderNo: string;
  reddOrderNo: string;
  status: PaymentOrderStatus;
  accountType: WalletAccountType;
  amountFen: number;
  cashierUrl?: string;
  expiresAt: string;
  createdAt: string;
  paidAt?: string;
};
export type OpenApiRechargeOrderAdmin = OpenApiRechargeOrder & {
  merchantId: string;
  merchantName: string;
  appId: string;
  userId: string;
  alipayTradeNo?: string;
  refundedAmountFen?: number;
};
export type OpenApiWebhookDelivery = {
  id: string;
  eventId: string;
  merchantId: string;
  merchantName: string;
  partnerOrderNo: string;
  reddOrderNo: string;
  status: 'pending' | 'succeeded' | 'failed';
  attempts: number;
  responseStatus?: number;
  lastError?: string;
  lastAttemptAt?: string;
  deliveredAt?: string;
  createdAt: string;
};
export type OpenApiApiCall = {
  id: string;
  merchantId?: string;
  merchantName?: string;
  keyId?: string;
  requestId: string;
  method: string;
  path: string;
  ipAddress?: string;
  partnerOrderNo?: string;
  resultCode: string;
  httpStatus: number;
  durationMs: number;
  createdAt: string;
};

export type FinancialOrderStatus = 'pending_review' | 'processing' | 'succeeded' | 'rejected' | 'failed';
export type AlipayPayoutProfile = {
  status: 'unconfigured' | 'pending' | 'verified';
  payeeIdentityMasked?: string;
  payeeNameMasked?: string;
  verifiedAt?: string;
  updatedAt?: string;
};
export type WithdrawalOrder = {
  membershipCouponId?: string;
  feeDiscountFen?: number;
  id: string;
  withdrawalNo: string;
  userId: string;
  accountType: WalletAccountType;
  amountFen: number;
  feeFen: number;
  payoutFen: number;
  status: FinancialOrderStatus;
  payeeIdentityMasked: string;
  payeeNameMasked: string;
  alipayOrderId?: string;
  alipayPayFundOrderId?: string;
  providerStatus?: string;
  failureCode?: string;
  failureMessage?: string;
  reviewReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  completedAt?: string;
  createdAt: string;
};
export type OriginalRefundOrder = {
  id: string;
  refundNo: string;
  userId: string;
  paymentOrderNo: string;
  accountType: WalletAccountType;
  amountFen: number;
  status: FinancialOrderStatus;
  alipayTradeNo: string;
  providerRefundFeeFen?: number;
  providerStatus?: string;
  failureCode?: string;
  failureMessage?: string;
  reviewReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  completedAt?: string;
  createdAt: string;
};
export type AuthUser = {
  id: string;
  status: 'active' | 'restricted' | 'suspended' | 'closed';
  role: 'user' | 'viewer' | 'support' | 'operations' | 'risk' | 'finance' | 'security_admin' | 'owner';
  phoneMasked: string;
  reviewMode: boolean;
  createdAt: string;
};
export type NativeAuthSession = {
  user: AuthUser;
  accessToken: string;
  expiresAt: string;
};

export type MarketplaceListingStatus = 'active' | 'pending_delivery' | 'sold' | 'cancelled';
export type MarketplaceListing = {
  dotaMetadata?: DotaMetadata;
  referencePriceFen?: number;
  id: string;
  sellerId: string;
  sellerDisplayName: string;
  assetId: string;
  game: GameId;
  marketHashName: string;
  displayName: string;
  imageUrl: string;
  itemType?: string;
  wear?: string;
  floatValue?: number;
  paintSeed?: number;
  priceFen: number;
  status: MarketplaceListingStatus;
  automaticDeliveryAuthorized?: boolean;
  counterOffersAllowed: boolean;
  createdAt: string;
  updatedAt: string;
};
export type MarketplaceOrderStatus = 'pending_delivery' | 'delivered' | 'completed' | 'cancelled' | 'refunded' | 'disputed';
export type MarketplaceDeliveryStatus = 'queued' | 'sending' | 'needs_confirmation' | 'sent' | 'cancelling' | 'failed' | 'unknown';
export type MarketplaceOfferInitiatorRole = 'buyer' | 'seller';
export type MarketplaceOrder = {
  id: string;
  orderType: 'purchase' | 'buy_order';
  buyOrderId?: string;
  listingId: string;
  buyerId: string;
  sellerId: string;
  amountFen: number;
  feeFen: number;
  sellerReceivableFen: number;
  discountFen?: number;
  couponId?: string;
  status: MarketplaceOrderStatus;
  listing: MarketplaceListing;
  offerInitiatorRole: MarketplaceOfferInitiatorRole;
  steamOfferId?: string;
  steamOfferState?: number;
  steamOfferStateUpdatedAt?: string;
  deliveryStatus?: MarketplaceDeliveryStatus;
  deliveryErrorCode?: string;
  deliveryUpdatedAt?: string;
  createdAt: string;
  manualCancelAvailableAt: string;
  autoCancelAt: string;
  updatedAt: string;
  completedAt?: string;
  sellerProceedsReleaseAt?: string;
  sellerProceedsReleasedAt?: string;
};
export type MarketplaceOrderList = { buying: MarketplaceOrder[]; selling: MarketplaceOrder[] };
export type PurchaseRecordsQuery = { query?:string; status?:'pending'|'completed'|'cancelled'|'after_sale'; game?:GameId; cursor?:string };
export type PurchaseRecordsPage = { items:MarketplaceOrder[]; nextCursor?:string };
export type MarketplaceBuyOrderStatus = 'active' | 'filled' | 'cancelled';
export type MarketplaceBuyOrder = {
  id: string;
  buyerId: string;
  buyerDisplayName: string;
  game: GameId;
  catalogItemId: string;
  marketHashName: string;
  displayName: string;
  imageUrl: string;
  wear?: string;
  maxPriceFen: number;
  quantity: number;
  filled: number;
  status: MarketplaceBuyOrderStatus;
  createdAt: string;
  updatedAt: string;
};
export type MarketplaceCounterOfferStatus = 'active' | 'accepted' | 'rejected' | 'withdrawn';
export type MarketplaceCounterOffer = {
  id: string;
  listingId: string;
  buyerId: string;
  buyerDisplayName: string;
  sellerId: string;
  amountFen: number;
  status: MarketplaceCounterOfferStatus;
  resubmissionBlocked: boolean;
  listing: MarketplaceListing;
  createdAt: string;
  updatedAt: string;
};
export type MarketplaceCounterOfferInbox = {
  buying: MarketplaceCounterOffer[];
  selling: MarketplaceCounterOffer[];
};
export type MarketplacePreferences = {
  counterOffersEnabled: boolean;
  tradeNotificationsEnabled: boolean;
  cooldownNotificationsEnabled: boolean;
  compactInventoryCards: boolean;
};
export type SmsCodeRequestResult = {
  phoneMasked: string;
  expiresInSeconds: number;
  resendAfterSeconds: number;
  debugCode?: string;
};
export type AdminPaymentOrderSummary = PaymentOrder & { notifyReceivedAt?: string };
export type OutboxEvent<TPayload = Record<string, unknown>> = {
  eventId: string;
  eventType: string;
  aggregateType: string;
  aggregateId: string;
  occurredAt: string;
  schemaVersion: number;
  payload: TPayload;
};

export const defaultWebPresentation = {
  heroImageUrl: '',
  complianceNotice: '本网站响应国家号召促进国家文旅事业发展，本网站严格接受人民银行监管反洗钱市场监管总局反诈骗',
  featuredTitle: '换一批，看看新的选择',
  featuredSubtitle: '',
};

export type WebPlatformSettings = {
  heroImageUrl?: string;
  complianceNotice?: string;
  featuredTitle?: string;
  featuredSubtitle?: string;
  siteName: string;
  heroEyebrow: string;
  heroTitle: string;
  heroSubtitle: string;
  heroVideoUrl: string;
  heroVideoPosterUrl: string;
  brandLogoUrl: string;
  appDownloadUrl: string;
  appQrCodeUrl: string;
  marketFallbackImageUrl: string;
  marketEnabled: boolean;
  sellEnabled: boolean;
  maintenanceMode: boolean;
  marketPageSize: number;
  footerCompany: string;
  icpRecord: string;
  policeRecord: string;
  supportText: string;
};
export type AppServiceActionKind = 'route' | 'ticket';
export type AppServiceItem = {
  id: string;
  sectionId: string;
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  badge: string;
  actionKind: AppServiceActionKind;
  actionValue: string;
  requiresOrder: boolean;
  enabled: boolean;
  sortOrder: number;
};
export type AppServiceSection = {
  id: string;
  title: string;
  subtitle: string;
  enabled: boolean;
  sortOrder: number;
};
export type AppServiceCatalog = {
  sections: AppServiceSection[];
  items: AppServiceItem[];
  updatedAt: string;
};
export type AppFavorite = {
  targetKey: string;
  catalogItemId: string;
  marketHashName: string;
  displayName: string;
  imageUrl: string;
  createdAt: string;
};
export type AppCoupon = {
  id: string;
  title: string;
  description: string;
  creditFen: number;
  status: 'active' | 'disabled';
  quota: number;
  claimedCount: number;
  startsAt?: string;
  endsAt?: string;
};
export type AdminAppCoupon = AppCoupon & { code: string };
export type AppCouponRedemption = {
  id: string;
  couponId: string;
  title: string;
  creditFen: number;
  status: 'pending' | 'credited';
  redeemedAt: string;
};
export type AdminDashboard = {
  period: { from: string; to: string; timezone: 'Asia/Shanghai' };
  today: {
    rechargeFen: number;
    rechargeCount: number;
    payoutFen: number;
    payoutCount: number;
    feeFen: number;
    netCashflowFen: number;
  };
  balances: { availableFen: number; frozenFen: number };
  pending: {
    consumerWithdrawals: number;
    consumerWithdrawalFen: number;
    merchantWithdrawals: number;
    merchantWithdrawalFen: number;
    approvedMerchantWithdrawals: number;
    approvedMerchantWithdrawalFen: number;
    refunds: number;
    refundFen: number;
    outbox: number;
  };
  last7Days: Array<{ date: string; rechargeFen: number; payoutFen: number; feeFen: number }>;
  users: number;
  activeMerchants: number;
  updatedAt: string;
};
export type UserAccountKind = 'consumer' | 'merchant_service';
export type MerchantServiceAccount = {
  userId: string;
  reddAccountNo: string;
  displayName: string;
  accountKind: 'merchant_service';
  loginEnabled: false;
  createdAt: string;
};
export type AdminUserSummary = { id: string; reddAccountNo: string; accountKind: UserAccountKind; displayName?: string; status: string; role: AuthUser['role']; phoneMasked?: string; createdAt: string; lastLoginAt?: string; steamId?: string; steamStatus?: string; inventoryItems: number };
export type AdminLoginSessionSummary = { id: string; userId: string; phoneMasked?: string; createdAt: string; lastSeenAt: string; expiresAt: string; revokedAt?: string; ipAddress?: string; userAgent?: string };
export type AdminInventorySummary = { userId: string; steamId: string; assetId: string; displayName: string; tradable: boolean; priceCny?: number; importedAt: string };
export type AdminOrderSummary = { id: string; type: 'purchase' | 'buy_order'; status: string; amountFen: number; buyerId: string; sellerId: string; createdAt: string };
export type AdminPage<T> = {
  items: T[];
  total: number;
  hasMore: boolean;
  nextCursor?: string;
};
export type AdminWalletSummary = {
  accountType: WalletAccountType;
  availableFen: number;
  frozenFen: number;
  version: number;
};
export type AdminUserDetail = AdminUserSummary & {
  wallets: AdminWalletSummary[];
  activeSessions: number;
  paymentOrders: number;
  merchantMallOrders?: number;
};
export type AdminMerchantFinanceSummary = {
  rechargeCount: number;
  rechargeFen: number;
  withdrawalCount: number;
  withdrawalGrossFen: number;
  payoutFen: number;
  merchantFeeFen: number;
  alipayCostFen: number;
  profitFen: number;
};
export type AdminMerchantFinanceSeriesPoint = AdminMerchantFinanceSummary & { period: string };
export type AdminMerchantLedgerEntry = {
  id: string;
  kind: 'recharge' | 'withdrawal';
  referenceNo: string;
  partnerOrderNo?: string;
  accountType: WalletAccountType;
  grossFen: number;
  payoutFen?: number;
  merchantFeeFen?: number;
  alipayCostFen?: number;
  profitFen?: number;
  transferReference?: string;
  occurredAt: string;
};
export type AdminMerchantFinanceDetail = {
  merchant: {
    id: string;
    appId: string;
    name: string;
    userId: string;
    reddAccountNo: string;
    accountType: WalletAccountType;
    mode: OpenApiMerchantMode;
    status: OpenApiMerchantStatus;
    withdrawalFeeBps: number;
    createdAt: string;
  };
  wallet?: AdminWalletSummary;
  period: { from: string; to: string; granularity: 'day' | 'month' };
  selected: AdminMerchantFinanceSummary;
  lifetime: AdminMerchantFinanceSummary;
  series: AdminMerchantFinanceSeriesPoint[];
  ledger: AdminPage<AdminMerchantLedgerEntry>;
};
export type AdminPaymentOrderDetail = AdminPaymentOrderSummary & {
  phoneMasked?: string;
  wallet?: AdminWalletSummary;
  ledgerEntryId?: string;
};

export type MerchantWithdrawalStatus = 'pending_review' | 'approved' | 'transferred' | 'rejected' | 'cancelled';
export type MerchantWithdrawalRequest = {
  id: string;
  requestNo: string;
  merchantId: string;
  merchantName: string;
  accountType: WalletAccountType;
  partnerOrderNo?: string;
  amountFen: number;
  feeBps: number;
  feeFen: number;
  payoutFen: number;
  payeeIdentityMasked: string;
  payeeNameMasked: string;
  payeeIdentity?: string;
  payeeName?: string;
  status: MerchantWithdrawalStatus;
  reviewReason?: string;
  reviewedAt?: string;
  cancelReason?: string;
  cancelledAt?: string;
  transferReference?: string;
  transferNote?: string;
  proofFileName?: string;
  proofContentType?: string;
  proofSizeBytes?: number;
  proofSha256?: string;
  transferredAt?: string;
  createdAt: string;
};
export type MerchantPortalIdentity = { merchantId: string; merchantName: string; username: string; accountType: WalletAccountType; withdrawalFeeBps: number };
export type MerchantPortalOrder = {
  provider?: PaymentChannel;
  providerTradeNo?: string;
  paymentDetails?: ShouqianbaPaymentDetails;
  partnerOrderNo: string;
  reddOrderNo: string;
  status: PaymentOrderStatus;
  amountFen: number;
  createdAt: string;
  expiresAt: string;
  paidAt?: string;
};
export type MerchantPayoutProfile = { payeeIdentity: string; payeeName: string; updatedAt: string };
export type MerchantPortalDashboard = {
  identity: MerchantPortalIdentity;
  wallet: AdminWalletSummary;
  period: { from: string; to: string };
  orders: { total: number; paid: number; paidAmountFen: number; refundedAmountFen: number; netPaidAmountFen: number; pending: number; refunded: number };
  withdrawals: { total: number; pendingReviewFen: number; approvedFen: number; transferredFen: number; feesFen: number };
};

export type SupportArticleStatus = 'draft' | 'published' | 'archived';
export type SupportArticle = {
  id: string;
  category: string;
  title: string;
  answer: string;
  keywords: string[];
  status: SupportArticleStatus;
  priority: number;
  source: 'manual' | 'import';
  viewCount: number;
  helpfulCount: number;
  unhelpfulCount: number;
  createdAt: string;
  updatedAt: string;
};
export type SupportCategory = { name: string; articleCount: number; icon: string };
export type SupportHome = {
  categories: SupportCategory[];
  popularArticles: SupportArticle[];
  searchResults: SupportArticle[];
  openTicketCount: number;
  waitingConversationCount: number;
};
export type SupportConversationStatus = 'bot' | 'waiting' | 'active' | 'resolved' | 'closed';
export type SupportMessageSender = 'user' | 'bot' | 'agent' | 'system';
export type SupportMessage = {
  id: string;
  conversationId: string;
  senderType: SupportMessageSender;
  senderId?: string;
  body: string;
  visibility: 'public' | 'internal';
  aiGenerated: boolean;
  articleId?: string;
  createdAt: string;
};
export const SUPPORT_AI_NOTICE_VERSION = 'deepseek-support-2026-09-09';
export type SupportConversation = {
  id: string;
  userId: string;
  subject: string;
  status: SupportConversationStatus;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  assignedTo?: string;
  orderId?: string;
  lastMessageAt: string;
  firstResponseAt?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
  messages?: SupportMessage[];
  userPhoneMasked?: string;
  aiConsentGranted?: boolean;
  aiConsentVersion?: string;
  orderSummary?: { id: string; status: string; amountFen: number; itemName: string };
};
export type SupportChatReply = {
  conversation: SupportConversation;
  message: SupportMessage;
  matchedArticle?: SupportArticle;
  handedToHuman: boolean;
};
export type SupportTicketStatus = 'pending_review' | 'approved' | 'assigned' | 'processing' | 'waiting_user' | 'resolved' | 'rejected' | 'closed';
export type SupportTicket = {
  /** Present only when the server supports user supplements. */
  canReply?: boolean;
  updates?: Array<{ id: string; body: string; createdAt: string }>;
  id: string;
  ticketNo: string;
  userId: string;
  type: 'trade' | 'funds' | 'account' | 'steam' | 'appeal' | 'report' | 'value_service' | 'delivery' | 'recruitment' | 'feedback' | 'other';
  subject: string;
  description: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: SupportTicketStatus;
  assignedTo?: string;
  orderId?: string;
  formData?: Record<string, unknown>;
  approvalNote?: string;
  approvedBy?: string;
  approvedAt?: string;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
  userPhoneMasked?: string;
  orderSummary?: { id: string; status: string; amountFen: number; itemName: string };
};
export type SupportAdminDashboard = {
  waitingConversations: number;
  activeConversations: number;
  pendingReviewTickets: number;
  processingTickets: number;
  resolvedToday: number;
  publishedArticles: number;
};
export type SupportAiSuggestion = {
  reply: string;
  riskFlags: string[];
  sources: Array<{ articleId: string; title: string }>;
  model: string;
};
export type * from './admin-console.js';
export type * from './roll.js';
export type * from './legal.js';
export const adminConsoleModules = ['users', 'orders', 'inventory', 'listings', 'buy-orders', 'counter-offers', 'payments', 'withdrawals', 'refunds', 'ledger', 'wallets', 'tickets', 'messages', 'coupons', 'redemptions', 'sessions', 'audit', 'jobs', 'deletions', 'cashflow', 'fees', 'merchant-payouts', 'conversations', 'articles'] as const;
export type * from './rewards-admin.js';

export type CouponPackage = { id: string; priceYuan: number; coupons: {thresholdYuan:number; discountYuan:number; count:number}[] };
export type CouponPackageSettings = { purchaseEnabled?: boolean; validDays: number; coinRatio: number; packages: CouponPackage[] };
export const defaultCouponPackages: CouponPackageSettings = {validDays:365,coinRatio:1,packages:[
  {id:'100',priceYuan:100,coupons:[{thresholdYuan:1999,discountYuan:20,count:5}]},
  {id:'30',priceYuan:30,coupons:[{thresholdYuan:1999,discountYuan:20,count:1},{thresholdYuan:999,discountYuan:10,count:1}]},
  {id:'10',priceYuan:10,coupons:[{thresholdYuan:999,discountYuan:10,count:1}]},
  {id:'2',priceYuan:2,coupons:[{thresholdYuan:199,discountYuan:2,count:1}]},
  {id:'1',priceYuan:1,coupons:[{thresholdYuan:99,discountYuan:1,count:1}]},
]};
// Validate persisted settings as well as new administrator input before quoting or issuing value.
export function assertCouponPackageSettings(settings:CouponPackageSettings){
  const integer=(value:number,min:number,max:number)=>Number.isSafeInteger(value)&&value>=min&&value<=max;
  if(!settings||!integer(settings.validDays,1,365)||settings.coinRatio!==1||
    (settings.purchaseEnabled!==undefined&&typeof settings.purchaseEnabled!=='boolean')||
    !Array.isArray(settings.packages)||settings.packages.length<1||settings.packages.length>20)throw new Error('invalid_coupon_settings');
  const ids=new Set<string>(),prices=new Set<number>();
  for(const pack of settings.packages){
    if(!pack||typeof pack.id!=='string'||!/^[a-zA-Z0-9-]{1,30}$/.test(pack.id)||ids.has(pack.id)||
      !integer(pack.priceYuan,1,10000)||prices.has(pack.priceYuan)||!Array.isArray(pack.coupons)||pack.coupons.length<1||pack.coupons.length>20)throw new Error('invalid_coupon_settings');
    ids.add(pack.id);prices.add(pack.priceYuan);
    let faceValue=0;
    for(const coupon of pack.coupons){
      if(!coupon||!integer(coupon.thresholdYuan,1,1000000)||!integer(coupon.discountYuan,1,10000)||
        !integer(coupon.count,1,1000)||coupon.discountYuan>=coupon.thresholdYuan)throw new Error('invalid_coupon_settings');
      faceValue+=coupon.discountYuan*coupon.count;
    }
    if(faceValue!==pack.priceYuan)throw new Error('invalid_coupon_settings');
  }
  if(!prices.has(1))throw new Error('invalid_coupon_settings');
}
export function allocateCouponPackages(amountYuan:number,settings:CouponPackageSettings=defaultCouponPackages){
  if(!Number.isSafeInteger(amountYuan)||amountYuan<1||amountYuan>10000)throw new Error('invalid_coupon_amount');
  assertCouponPackageSettings(settings);
  let remaining=amountYuan;
  const selected: {package:CouponPackage;count:number}[]=[];
  for(const pack of [...settings.packages].sort((a,b)=>b.priceYuan-a.priceYuan)){
    if(!Number.isSafeInteger(pack.priceYuan)||pack.priceYuan<=0)throw new Error('invalid_coupon_package');
    const count=Math.floor(remaining/pack.priceYuan);if(count){selected.push({package:pack,count});remaining-=count*pack.priceYuan;}
  }
  if(remaining)throw new Error('coupon_amount_not_covered');
  return {amountYuan,coins:amountYuan*settings.coinRatio,selected,coupons:selected.flatMap(row=>row.package.coupons.map(coupon=>({...coupon,count:coupon.count*row.count})))};
}
export { groupLevels, groupStatusLabels } from './group-buy';
export type { GroupStatus, GroupDraft, GroupOffer, GroupProposal, GroupCustody, GroupSale, GroupState, GroupView, GroupPage, GroupLevel, GroupActivity, GroupTradingInfo, GroupQuote, GroupOfferPage, GroupShareTrade, GroupTradePage } from './group-buy';

export type ShoppingCoupon = {id:string;thresholdFen:number;discountFen:number;status:'available'|'reserved'|'used'|'expired'|'revoked';expiresAt:string;orderId?:string;source?:'package'|'membership';environment?:'Production'|'Sandbox'};
export type CollectionAccount = {coins:number;coupons:ShoppingCoupon[]};
